import { createContext, useContext, useState } from 'react'
import { useAuthStore } from 'context/AuthStore'
import { ClientService, SalesService, SeoService } from 'lib/api'
import { useResErrors, useRoutes } from 'hooks'
import { GENDERS, LANGUAGES } from 'constants/client'
import { LocalStorageService } from 'lib/utils'
import {
  NEW_SEO,
  NEW_WL,
  SELECTION_STEP_PATH_LOADED
} from 'routes/constants.routes'
import useSeoApis from 'hooks/useSeoApis'
import { useModalbar } from '../../../../modules/Modalbar'

export const SelectionStoreContext = createContext({
  selectionStore: {},
  selectionStoreFunc: {}
})
const INITIAL_RSE = {
  gender: null,
  visitType: null,
  budget: null,
  approximateBuy: null,
  prepayment: 0,
  carOwnership: null,
  creditProcent: 0,
  paymentType: null,
  additionalInfo: '',
  isTradeIn: false
}
const SelectionStoreProviderManager = ({ homePrData, ...props }) => {
  const {
    authStore,
    authStoreFunc: { authManagerInSeo }
  } = useAuthStore()
  const { standardCheck, silentCheck } = useResErrors()
  const { callModalbar } = useModalbar()
  const { seoLoading, getStat } = useSeoApis()
  const [loading, setLoading] = useState(false)
  const [queue, setQueue] = useState(0)
  const { goTo } = useRoutes()
  const [status, setStatus] = useState(null)
  const [buttonClicked, setButtonClicked] = useState(false)

  const getSeoQueue = async () => {
    await getStat({
      onSuccess: res => {
        const { data } = res
        setQueue(data.NORMAL + data.PRIORITY)
      },
      onFail: () => {
        setQueue(0)
      }
    })
  }

  const getStatus = async () => {
    let status = null

    await SeoService.getStatus(authStore.seoToken)().then(res =>
      standardCheck(
        res,
        () => {
          const { data } = res
          if (data.code === 4) {
            setStatus(null)
            status = data
            LocalStorageService.removeDataFromSeo()
          } else if (data?.Fields && [5, 6].includes(data?.code)) {
            const obj = {
              ...data,
              clientFields: {
                phoneNumber: data?.Fields[1]?.Value?.toString()?.toPhone(),
                firstName: data?.Fields[0]?.Value,
                fromSeo: true,
                gender: GENDERS.find(
                  ({ title }) => title === data?.Fields[3]?.Value
                )?.code,
                language: LANGUAGES.find(
                  ({ title }) => title === data?.Fields[2]?.Value
                )?.code
              }
            }
            setStatus(obj)
            status = obj
          } else {
            setStatus(null)
            status = null
            callModalbar(
              'Проблема СЭО , ' + data.code,
              data.error ??
                '' + ' .При закрытии будет попытка переавторизации в СЭО',
              {
                variant: 'error',
                onCloseAction: async () => {
                  setLoading(true)
                  await authManagerInSeo()
                  setLoading(false)
                }
              }
            )
          }
        },
        {
          onFail: () => {
            setStatus(null)
            status = null
          }
        }
      )
    )
    return status
  }

  const inviteClient = async () => {
    if (buttonClicked) {
      return
    }

    setLoading(true)
    setButtonClicked(true)

    const st = await getStatus()
    const token = authStore.seoToken

    if (st?.code === 4) {
      await SeoService.inviteClient(token)().then(res =>
        standardCheck(
          res,
          async () => {
            const st = await getStatus()

            if (st?.clientFields) {
              const phone = st?.clientFields?.phoneNumber?.toPhone()

              createSale(st, () => reassignActiveSale(phone, st?.TicketNum))
            } else {
              callModalbar(
                'Проблема СЭО , Возможно нет данных',
                st +
                  ' .При закрытии будет попытка переавторизации в СЭО (inviteClient 4) ',
                {
                  variant: 'error',
                  onCloseAction: async () => {
                    setLoading(true)
                    await authManagerInSeo()
                    setLoading(false)
                  }
                }
              )
            }
          },
          {
            onFail: () => {}
          }
        )
      )
    } else if ([5, 6].includes(st?.code)) {
      if (st?.clientFields) {
        const phone = st?.clientFields?.phoneNumber?.toPhone()

        createSale(st, () => reassignActiveSale(phone, st?.TicketNum))
      } else {
        callModalbar(
          'Проблема СЭО , Возможно нет данных',
          st +
            ' .При закрытии будет попытка переавторизации в СЭО (inviteClient)' +
            st?.code,
          {
            variant: 'error',
            onCloseAction: async () => {
              setLoading(true)
              await authManagerInSeo()
              setLoading(false)
            }
          }
        )
      }
    }
    setLoading(false)
  }

  const createSale = async (seoClient, cb = () => {}) => {
    setLoading(true)

    await ClientService.createClient({
      firstName: seoClient?.clientFields?.firstName,
      phoneNumber: seoClient?.clientFields?.phoneNumber?.toPhone(),
      gender: seoClient?.clientFields?.gender,
      language: seoClient?.clientFields?.language
    }).then(res =>
      standardCheck(
        res,
        async () => {
          const { id, ...client } = res.data
          setLoading(true)

          await SalesService.createSale({
            client: {
              ...client,
              clientId: id
            },
            seoNumber: seoClient.TicketNum,
            seoId: seoClient.Id,
            pin: seoClient.pincode,
            isNewSeo: true,
            commentsRSE: JSON.stringify(INITIAL_RSE)
          }).then(res =>
            standardCheck(
              res,
              () => {
                setLoading(false)
                SalesService.updateStatusTrack(res?.data?.id, Number(120)).then(
                  res => {
                    silentCheck(res, cb, {
                      onFail: cb
                    })
                  }
                )
                LocalStorageService.setActiveSelectionId(res.data.id)

                goTo(SELECTION_STEP_PATH_LOADED(res.data.id)[NEW_SEO])
              },
              {
                onFail: () => {
                  setLoading(false)
                }
              }
            )
          )
        },
        {
          onFail: () => {
            setLoading(false)
          }
        }
      )
    )
  }

  const reassignActiveSale = async (phone, seoNumber, cb = () => {}) => {
    setLoading(true)

    await SalesService.reassignActiveSale(phone, seoNumber).then(res =>
      silentCheck(
        res,
        () => {
          if (res?.data?.id) {
            setLoading(false)
            SalesService.updateStatusTrack(res?.data?.id, Number(120))

            goTo(SELECTION_STEP_PATH_LOADED(res.data.id)[NEW_SEO])
          } else {
            cb()
          }
        },
        {
          onFail: cb
        }
      )
    )
  }
  const goToNewWl = () => {
    goTo(SELECTION_STEP_PATH_LOADED()[NEW_WL])
  }

  const selectionStore = {
    queue,
    loading: loading || seoLoading,
    status,
    seoActive: homePrData.homeStore.seoActive
  }
  const selectionStoreFunc = {
    inviteClient,
    getSeoQueue,
    getStatus,
    goToNewWl,
    haveActiveSale: homePrData.homeStoreFunc.haveActiveSale,
    getActiveSeo: homePrData.homeStoreFunc.getActiveSeo
  }
  return (
    <SelectionStoreContext.Provider
      value={{ selectionStore, selectionStoreFunc }}
    >
      {props.children}
    </SelectionStoreContext.Provider>
  )
}
export default SelectionStoreProviderManager

export const useSelectionStoreManager = () => useContext(SelectionStoreContext)
